// Axios instance shared by every feature module that talks to the
// Forge Ultra FastAPI backend.
//
// Responsibilities:
//   1. Attach the WordPress-issued JWT as `Authorization: Bearer <token>`
//      on every outbound request.
//   2. Refuse to transmit the token over plain HTTP in production — JWTs
//      are bearer credentials and must never travel over an unencrypted
//      channel (see the security checklist in the project brief).
//   3. Centralise 401 handling: on an Unauthorized response we clear the
//      cached token and bounce the user back to the WordPress login page
//      so they can refresh their session.
//
// Anything that needs to call the API should `import { apiClient } from
// '@/api/client'` (or the relative path) instead of constructing a new
// axios instance — that keeps the auth + error contract in one place.

import type {
    AxiosError} from 'axios';
import axios, {
    type AxiosInstance,
    type InternalAxiosRequestConfig,
} from 'axios';

import { bounceToLoginOnce, clearToken, getToken, setToken } from './auth-storage';

// Module augmentation: extend axios' request config with our own opt-out
// flag so a caller can ask the response interceptor NOT to auto-bounce
// to WP on a 401. Used by ProtectedRoute's /users/me probe — see
// api/users.ts (FetchCurrentUserOptions) and the response interceptor
// below. Adding the field on the canonical axios types means callers
// get full TS autocomplete without per-call casts.
//
// ``_retry`` is the standard axios convention for "this request was
// already retried after a silent refresh — do NOT retry it again,
// even if it 401s a second time". The leading underscore signals
// "framework internal, not for callers to set by hand".
declare module 'axios' {
    export interface AxiosRequestConfig {
        skipAuthBounce?: boolean;
    }
    export interface InternalAxiosRequestConfig {
        skipAuthBounce?: boolean;
        _retry?: boolean;
    }
}

// The WordPress URL the user is bounced to whenever we detect a missing
// or expired token. Configurable per-environment via VITE_WP_LOGIN_URL so
// we can point at a staging WP instance without rebuilding the client.
//
// Default points at the canonical /sso-bridge path handled by the
// ki-wealth-auth-bridge.php WP plugin (v2.1.0+): when an
// already-logged-in WP user lands there, the plugin mints a fresh JWT
// and 302s back to /login-bridge?token=<JWT>, completing the handshake
// transparently. The plugin's `init` hook ignores any other URL, so
// this string MUST match the plugin's expected path exactly.
const WP_LOGIN_URL: string =
    import.meta.env.VITE_WP_LOGIN_URL ??
    'https://www.ki-wealth.com/sso-bridge';

// Base URL of the API. We deliberately fail loudly in production if it is
// missing or non-HTTPS — JWT-bearing traffic over http:// is the single
// most common cause of token leakage.
//
// The backend mounts every router under ``/api/v1`` in main.py
// (``app.include_router(auth_router, prefix="/api/v1/auth")`` etc.).
// We append this prefix in code (rather than burning it into the env
// var) so the deployment configuration only ever cares about the
// backend HOST — bumping the API version touches a single constant
// here instead of every .env file and every hosting-provider dashboard.
const REQUIRED_API_PREFIX = '/api/v1';

// Hosts where an ``http://`` API base URL is acceptable even when the
// bundle was built with ``NODE_ENV=production`` (i.e. ``import.meta.env.PROD``
// is true). This is the "local prod build" escape hatch for smoke-
// testing ``vite build`` + ``vite preview`` against a plain-HTTP local
// backend (``http://localhost:8000`` or ``http://host.docker.internal:8000``
// when running the backend in Docker). Any host that resolves to the
// loopback interface or the Docker host bridge stays on the same
// machine, so a JWT cannot leak onto a hostile network. Every other
// host MUST use https:// — that is the real-deploy guard.
const LOCAL_DEV_HOSTS = new Set<string>([
    'localhost',
    '127.0.0.1',
    '::1',
    '[::1]',
    'host.docker.internal',
    '0.0.0.0',
]);

function isLocalDevHost(rawUrl: string): boolean {
    try {
        const host = new URL(rawUrl).hostname.toLowerCase();
        return LOCAL_DEV_HOSTS.has(host);
    } catch {
        // Malformed URL — let the caller surface the parse error
        // downstream (axios will reject the first request). Returning
        // ``false`` keeps the HTTPS guard intact for the obviously-bad
        // case.
        return false;
    }
}

function resolveBaseUrl(): string {
    // ``VITE_API_URL`` holds ONLY the backend host (e.g.
    // ``https://forge-api.ki-wealth.com``). The ``/api/v1`` versioning
    // segment is appended below, in code, so the hosting-provider
    // configuration never has to know about it.
    //
    // In local dev we leave the value empty so axios issues same-origin
    // requests like ``/api/v1/auth/...`` that the Vite dev proxy then
    // forwards to ``http://localhost:8000`` (see vite.config.ts).
    const raw = (import.meta.env.VITE_API_URL ?? '').trim();

    if (raw === '') {
        if (import.meta.env.PROD) {
            throw new Error(
                'VITE_API_URL is not defined. Set it in .env.production ' +
                    'or in the Cloudflare Pages → Settings → Environment ' +
                    'variables dashboard.',
            );
        }
        // Dev: same-origin relative path, served via the Vite proxy.
        return REQUIRED_API_PREFIX;
    }

    // Enforce HTTPS in production builds, EXCEPT for known loopback /
    // docker-host hostnames where http:// is the expected local-build
    // configuration for ``vite preview`` smoke tests.
    if (
        import.meta.env.PROD &&
        !raw.startsWith('https://') &&
        !isLocalDevHost(raw)
    ) {
        throw new Error(
            `Refusing to start: VITE_API_URL must use HTTPS in production (got "${raw}").`,
        );
    }

    // Strip a trailing slash so the prefix-check below is unambiguous and
    // so axios does not end up issuing URLs with a double slash
    // (``//auth/login-bridge``) which some gateways treat as a different
    // path than ``/auth/login-bridge``.
    const trimmed = raw.replace(/\/+$/, '');

    // Normalise: ensure the ``/api/v1`` versioning segment is present.
    // Most deployments will set ``VITE_API_URL`` to a bare host and rely
    // on the append below. We still tolerate a value that already ends
    // with the prefix so a manually-overridden Cloudflare dashboard
    // value keeps working without code changes.
    if (trimmed.endsWith(REQUIRED_API_PREFIX)) {
        return trimmed;
    }
    return `${trimmed}${REQUIRED_API_PREFIX}`;
}

// Loud one-time banner so the baked-in URL is visible in the browser
// DevTools console on every load. If the SSO handshake ever 404s again,
// the first thing to check is what this line prints — it is the exact
// URL prefix every API call goes to.
const RESOLVED_BASE_URL = resolveBaseUrl();
// eslint-disable-next-line no-console
console.info('[apiClient] baseURL =', RESOLVED_BASE_URL);

export const apiClient: AxiosInstance = axios.create({
    baseURL: RESOLVED_BASE_URL,
    timeout: 15_000,
    headers: { 'Content-Type': 'application/json' },
    // ``withCredentials: true`` is required for the ``forge_session``
    // httpOnly cookie set by POST /api/v1/auth/login-bridge to be sent
    // on subsequent cross-site requests (the SPA on
    // forge-ultra.ki-wealth.com talking to the backend at
    // forge-api.ki-wealth.com). The cookie is set with SameSite=None;
    // Secure on the backend, which is the matching attribute combination
    // required by every modern browser for cross-site cookie
    // transmission.
    withCredentials: true,
});

// --- Request interceptor -------------------------------------------------
// Attach the JWT to every request. We keep this here (rather than in each
// feature hook) so a future swap to httpOnly cookies only touches one file.
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
});

// --- Response interceptor ------------------------------------------------
// Token expiry handling.
//
// Flow on 401:
//   1. If the failing request is a refresh / login-bridge / opt-out: skip.
//   2. If we've ALREADY retried this request (``_retry === true``):
//      the refresh itself failed, or the refreshed token still 401'd →
//      bounce to WP login (with the ``bounceToLoginOnce`` loop guard).
//   3. Otherwise: silently call POST /auth/refresh. If it succeeds,
//      replay the original request with the new token.
//
// Single-flight refresh: when the dashboard fans out three calls on
// mount and all three 401 at once, only ONE /auth/refresh call is made;
// the other two ``await`` the same Promise and then replay with the
// updated token. Without this, three parallel rotations would race —
// each one revoking the previous — and only the last token would
// survive, breaking the silent-refresh contract.
let isRedirectingToLogin = false;
let refreshInFlight: Promise<string | null> | null = null;

async function refreshTokenSingleflight(): Promise<string | null> {
    // If a refresh is already running, every concurrent caller awaits
    // the same promise. We import lazily to break the cycle
    // (api/auth.ts imports apiClient from this file).
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
        try {
            const { refreshAccessToken } = await import('./auth');
            const { access_token } = await refreshAccessToken();
            setToken(access_token);
            // eslint-disable-next-line no-console
            console.info('[apiClient] silent refresh succeeded.');
            return access_token;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[apiClient] silent refresh failed.', err);
            return null;
        } finally {
            // Release the slot AFTER the awaiters have read the value —
            // setting it back to null inside the IIFE means a new
            // concurrent caller starts a fresh refresh on the next 401,
            // which is what we want (one refresh per cycle of failures).
            refreshInFlight = null;
        }
    })();
    return refreshInFlight;
}

apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        if (error.response?.status !== 401) {
            return Promise.reject(error);
        }
        const original = error.config as InternalAxiosRequestConfig | undefined;
        if (!original) return Promise.reject(error);

        // /auth/login-bridge is the SSO handshake endpoint that
        // *establishes* the session. Its 401 means the WP-issued JWT
        // was rejected; login-success.tsx has its own retry/bounce
        // logic and is the sole owner of that error path.
        // /auth/refresh failing means our refresh cookie itself is
        // dead — handled in the ``bounce`` branch below; we must not
        // recursively call refresh on a refresh failure.
        const isAuthEndpoint =
            (original.url ?? '').includes('/auth/login-bridge') ||
            (original.url ?? '').includes('/auth/refresh');
        // ``skipAuthBounce`` only suppresses the OFF-SITE redirect.
        // Silent refresh is an in-page operation with no UX side
        // effects, so we MUST still attempt it even when the caller
        // (e.g. ProtectedRoute) wants to own the "no session" UX.
        // Conflating the two would mean ProtectedRoute's /users/me
        // probe sees every access-token expiry as a hard 401 and
        // renders the manual sign-in screen — which is exactly the
        // bug fixed here.
        const skipAuthBounce = original.skipAuthBounce === true;

        // 1) Try a silent refresh + retry, exactly once per request.
        //    Runs regardless of ``skipAuthBounce`` (see comment above).
        if (!isAuthEndpoint && !original._retry) {
            original._retry = true;
            const newAccess = await refreshTokenSingleflight();
            if (newAccess) {
                // Update the failed request's Authorization header to
                // the freshly-minted token and replay it. ``apiClient``
                // returns the resolved response so the original caller's
                // ``.then`` chain runs as if the refresh never happened.
                original.headers = original.headers ?? {};
                // ``set`` exists on AxiosHeaders (current axios versions);
                // fall back to direct assignment for any exotic header bag.
                if (typeof (original.headers as { set?: unknown }).set === 'function') {
                    (original.headers as { set: (k: string, v: string) => void }).set(
                        'Authorization',
                        `Bearer ${newAccess}`,
                    );
                } else {
                    (original.headers as Record<string, string>).Authorization =
                        `Bearer ${newAccess}`;
                }
                return apiClient(original);
            }
            // refresh returned null — fall through to the bounce branch.
        }

        // 2) Refresh failed (or wasn't applicable) → bounce to WP,
        //    UNLESS the caller opted out via skipAuthBounce.
        if (!isAuthEndpoint && !skipAuthBounce) {
            // eslint-disable-next-line no-console
            console.warn(
                '[apiClient] 401 from API and silent refresh failed; bouncing to WP.',
                {
                    url: original.url,
                    method: original.method,
                    detail: (error.response.data as { message?: string } | undefined)
                        ?.message,
                },
            );
            clearToken();
            // Guard against multiple in-flight 401s firing the redirect
            // repeatedly (e.g. if three concurrent requests all fail
            // refresh together).
            if (!isRedirectingToLogin && typeof window !== 'undefined') {
                isRedirectingToLogin = bounceToLoginOnce(WP_LOGIN_URL);
                // If bounceToLoginOnce returned false the loop guard
                // tripped — stay on the page and let the caller (e.g.
                // ProtectedRoute) react via the rejected promise below.
            }
        }
        return Promise.reject(error);
    },
);

// Convenience helper for components / route guards that just need to know
// "do we currently have a token?" without pulling in the storage module.
export function isAuthenticated(): boolean {
    return Boolean(getToken());
}
